import { useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { ChevronLeft, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import type { MiRegistroLegalResumen } from "./types";
import { Paso1Datos } from "./pasos/Paso1Datos";
import { Paso2Domicilio } from "./pasos/Paso2Domicilio";
import { Paso3Representantes } from "./pasos/Paso3Representantes";
import { Paso4Revision } from "./pasos/Paso4Revision";

/**
 * Orquestador del wizard de 4 pasos. URL: /mi-registro-legal/wizard?id=UUID&paso=N.
 * Cada paso es un componente independiente que recibe el RL actual y dos
 * callbacks: avanzar (al siguiente paso) y salir (al entry).
 */
export function MiRegistroLegalWizardPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const id = params.get("id") ?? "";
  const paso = Math.max(1, Math.min(4, Number(params.get("paso") ?? "1")));
  const correoUsuarioActual = auth.user?.profile?.email ?? null;

  // Carga el borrador por id (permite a Calidad abrir borradores ajenos).
  // Si la URL no trae id, el efecto de abajo redirige al entry.
  const qRl = useQuery<MiRegistroLegalResumen>({
    queryKey: ["mi-registro-legal", id],
    queryFn: async () => {
      const r = await api.get<MiRegistroLegalResumen>(`/api/mi-registro-legal/${id}`);
      return r.data;
    },
    enabled: !!id,
  });

  // Si no viene id, redirige al entry para crear borrador.
  useEffect(() => {
    if (!id) navigate("/mi-registro-legal", { replace: true });
  }, [id, navigate]);

  const irAlPaso = (n: number) => setParams({ id, paso: String(n) }, { replace: true });

  if (qRl.isLoading || !qRl.data) {
    return (
      <div className="flex items-center justify-center py-20 text-fg-subtle">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Cargando tu trámite…
      </div>
    );
  }

  const rl = qRl.data;
  const modoCalidad = !!rl.solicitanteCorreo && !!correoUsuarioActual && rl.solicitanteCorreo !== correoUsuarioActual;
  // Calidad regresa a su bandeja; el cliente regresa al resumen de su trámite.
  const salir = () => navigate(modoCalidad ? "/tramites-legales" : "/mi-registro-legal");

  // Si el RL ya no es editable (APROBADO, RECHAZADO, EN_REVISION_QA), regresa al entry.
  const editable = rl.estatus === "BORRADOR_INTERNO" || rl.estatus === "OBSERVADO";
  if (!editable) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Link
          to="/mi-registro-legal"
          className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-primary-strong"
        >
          <ChevronLeft className="h-4 w-4" />
          Volver al resumen
        </Link>
        <div className="rounded-lg border bg-estado-warn-bg p-4 text-sm text-estado-warn">
          Tu trámite ya no es editable en este momento. Estatus actual: <strong>{rl.estatus}</strong>.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Link
          to={modoCalidad ? "/tramites-legales" : "/mi-registro-legal"}
          className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-primary-strong"
        >
          <ChevronLeft className="h-4 w-4" />
          {modoCalidad ? "Volver a Trámites Legales" : "Volver al resumen"}
        </Link>
      </div>

      {modoCalidad && (
        <div className="flex items-start gap-2 rounded-lg border border-estado-warn/25 bg-estado-warn-bg px-4 py-3 text-sm text-estado-warn">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <div className="font-semibold">Estás editando el borrador de {rl.solicitanteCorreo}.</div>
            <div className="text-xs">
              Los cambios se guardan en bitácora a tu nombre. Avanza por los pasos para completar la
              información que el cliente no terminó.
            </div>
          </div>
        </div>
      )}

      <header>
        <h1 className="text-2xl font-semibold text-fg">Captura de Registro Legal</h1>
        <p className="text-sm text-fg-muted">
          Paso {paso} de 4. Puedes guardar tu avance y continuar después.
        </p>
      </header>

      <BarraProgreso pasoActual={rl.pasoActual} pasoVisible={paso} irAlPaso={irAlPaso} />

      <div>
        {paso === 1 && <Paso1Datos rl={rl} onSaved={() => irAlPaso(2)} onSalir={salir} />}
        {paso === 2 && (
          <Paso2Domicilio
            rl={rl}
            onSaved={() => irAlPaso(rl.tipoPersona === "MORAL" ? 3 : 4)}
            onSalir={salir}
            onAtras={() => irAlPaso(1)}
          />
        )}
        {paso === 3 && (
          <Paso3Representantes
            rl={rl}
            onSaved={() => irAlPaso(4)}
            onSalir={salir}
            onAtras={() => irAlPaso(2)}
          />
        )}
        {paso === 4 && (
          <Paso4Revision
            rl={rl}
            onAtras={() => irAlPaso(rl.tipoPersona === "MORAL" ? 3 : 2)}
            onSalir={salir}
          />
        )}
      </div>
    </div>
  );
}

function BarraProgreso({
  pasoActual,
  pasoVisible,
  irAlPaso,
}: {
  pasoActual: number;
  pasoVisible: number;
  irAlPaso: (n: number) => void;
}) {
  const pasos = [
    { n: 1, label: "Datos generales" },
    { n: 2, label: "Domicilio fiscal" },
    { n: 3, label: "Representantes" },
    { n: 4, label: "Revisión y envío" },
  ];
  return (
    <ol className="flex flex-wrap items-center gap-2 rounded-lg border bg-surface p-3 text-sm shadow-card">
      {pasos.map((p, i) => {
        const accesible = p.n <= pasoActual; // solo los pasos ya completados son clickeables
        const seleccionado = p.n === pasoVisible;
        const completado = p.n < pasoActual;
        const baseColor = seleccionado
          ? "bg-primary text-white"
          : completado
            ? "bg-estado-ok-bg text-estado-ok"
            : accesible
              ? "bg-estado-block-bg text-estado-block"
              : "bg-surface-2 text-fg-faint";
        return (
          <li key={p.n} className="flex items-center gap-1">
            <button
              type="button"
              disabled={!accesible}
              onClick={() => irAlPaso(p.n)}
              className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-xs font-medium transition ${baseColor} ${
                accesible && !seleccionado ? "hover:bg-surface-2" : ""
              }`}
            >
              <span className={`grid h-5 w-5 place-items-center rounded-full text-[10px] ${
                seleccionado ? "bg-surface/20 text-white" : "bg-surface text-fg-muted"
              }`}>
                {p.n}
              </span>
              {p.label}
            </button>
            {i < pasos.length - 1 && <span className="text-fg-faint">›</span>}
          </li>
        );
      })}
    </ol>
  );
}
